import { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext(undefined);
const STORAGE_KEY = 'vizcorn-theme';

const prefersDark = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

const getStoredTheme = () => {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : null;
};

const getInitialTheme = () => getStoredTheme() || (prefersDark() ? 'dark' : 'light');

/**
 * ThemeProvider
 * - First visit: follows the OS/browser color-scheme preference automatically.
 * - Once the user picks a theme via <ThemeToggle />, that choice is saved to
 *   localStorage and takes over — the site stops auto-following system
 *   changes until the user clears their preference.
 * - The actual light/dark colors are defined as CSS variable overrides
 *   under the `.dark` class in src/index.css; this provider just toggles
 *   that class on <html>.
 */
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(getInitialTheme);
  const [isManual, setIsManual] = useState(() => getStoredTheme() !== null);

  // Reflect the current theme on <html class="dark">
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  // Auto-follow system preference until the user manually chooses a theme
  useEffect(() => {
    if (isManual) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, [isManual]);

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      window.localStorage.setItem(STORAGE_KEY, next);
      setIsManual(true);
      return next;
    });
  };

  // Optional: let the user go back to "match system" behavior
  const useSystemTheme = () => {
    window.localStorage.removeItem(STORAGE_KEY);
    setIsManual(false);
    setTheme(prefersDark() ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, useSystemTheme, isManual }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a <ThemeProvider>');
  }
  return ctx;
};
